<template>
  <div class="asc-page-wrapper" data-automation-id="wrapper_page">
    <asc-navigation-wrapper
      :on-size-change="handleSizeChange"
      :collapsed="isCollapsed"
      :on-manage-gaf="onManageGaf"
      :router="router"
      :on-log-out="onLogOut"
      :profile="profile"
      :feature-flags="featureFlags"
      :global-filter-text="globalFilterText"
      :global-filter-tooltip="globalFilterTooltip"
      :global-filter-active="globalFilterActive"
      :global-filter-enabled="globalFilterEnabled"
      :global-filter-disabled="globalFilterDisabled"
      :global-filter-visible="globalFilterVisible"
      :user-roles="userRoles"
      :user-airlines="userAirlines"
      :user-permissions="userPermissions"
    ></asc-navigation-wrapper>
    <v-main
      ref="contentWrapper"
      :class="[
        $route.meta.fullscreen && $route.meta.fullscreen === true
          ? ''
          : 'asc-content-wrapper--container',
        isCollapsed ? 'collapsed' : 'full',
        hasScroll ? 'hasScroll' : '',
      ]"
    >
      <asc-content-wrapper>
        <asc-notification
          v-if="getDisplayAlert"
          class="insidePageContainer"
          :definition="getErrorText"
          :on-close="handleCloseAlert"
        ></asc-notification>
        <asc-notification
          v-if="getConnectivityAlertDefinitions.length > 0 && isAirlineUser"
          class="insidePageContainer"
          :definition="getConnectivityAlertDefinitions"
          :on-close="handleCloseConnectivityAlert"
        ></asc-notification>
        <div ref="content"><slot></slot></div>
      </asc-content-wrapper>
      <asc-footer
        :collapsed="isCollapsed"
        :text="footerText"
        :current-year="currentYear"
      ></asc-footer>
    </v-main>
  </div>
</template>

<script>
import AscApiFailurePageAlertPropsMixin from '@/components/mixins/AscApiFailurePageAlertPropsMixin'
import AscConnectivityMixin from '@/components/mixins/AscConnectivityMixin'
import AscFooter from '@/components/atoms/AscFooter'
import AscNavigationWrapper from '@/components/organisms/AscNavigationWrapper'
import AscContentWrapper from '@/components/organisms/AscContentWrapper'
import AscNotification from '@/components/molecules/AscNotification'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  name: 'AscPageWrapper',

  components: {
    AscFooter,
    AscNavigationWrapper,
    AscContentWrapper,
    AscNotification,
  },

  mixins: [AscApiFailurePageAlertPropsMixin, AscConnectivityMixin],

  props: {
    footerText: {
      type: String,
      required: true,
    },
    currentYear: {
      type: Number,
      required: true,
    },
    apiPageFailure: {
      type: Array,
      default: () => [],
    },
    connectivityIssues: {
      type: Array,
      default: () => [],
    },
    isAirlineUser: {
      type: Boolean,
      default: false,
    },
    onCloseAlert: {
      type: Function,
      default: () => {},
    },
    onManageGaf: {
      type: Function,
      default: () => {},
    },
    onLogOut: {
      type: Function,
      default: () => {},
    },
    profile: {
      type: Object,
      default: () => {},
    },
    featureFlags: {
      type: Object,
      default: () => {},
    },
    globalFilterText: {
      type: String,
      default: '',
    },
    globalFilterTooltip: {
      type: String,
      default: '',
    },
    globalFilterActive: {
      type: Boolean,
      default: false,
    },
    globalFilterEnabled: {
      type: Boolean,
      default: false,
    },
    globalFilterDisabled: {
      type: Boolean,
      default: false,
    },
    globalFilterVisible: {
      type: Boolean,
      default: false,
    },
    router: {
      type: Object,
      default: () => {},
    },
    userRoles: {
      type: Array,
      default: () => [],
    },
    userPermissions: {
      type: Array,
      default: () => [],
    },
    userAirlines: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      displayAlert: false,
      isCollapsed: true,
      hasScroll: false,
      pageAlertDefinitions: Array,
      connectivityCheck: [false, false],
      connectivityAirlines: [],
    }
  },

  computed: {
    getDisplayAlert() {
      return this.displayAlert
    },
    getConnectivityAlertDefinitions() {
      if (this.connectivityIssues.length === 0 || !this.isConnectivityActive)
        return []
      this.updatePhmForConnectivity()
      this.defaultDefinitions()
      return this.pageAlertDefinitions
    },
    isConnectivityActive() {
      return AppFeatureFlags.getConnectivityBanner()
    },
  },

  watch: {
    apiPageFailure(newValue) {
      this.displayAlert = newValue?.length > 0 || false
    },
  },
  mounted() {
    if (this.isConnectivityActive) this.getConnectivityCheckFromSessionStorage()
  },

  updated: function () {
    const contentWrapper = this.$refs.contentWrapper
    const content = this.$refs.content
    this.hasScroll =
      contentWrapper && content
        ? content.clientHeight > contentWrapper.clientHeight
        : false
  },
  methods: {
    handleCloseAlert() {
      this.displayAlert = false
      if (this.onCloseAlert) {
        this.onCloseAlert()
      }
    },
    handleSizeChange() {
      this.isCollapsed = !this.isCollapsed
    },
    updatePhmForConnectivity() {
      const nonPhmAirlines = this.profile.phmAirlines.filter(
        airline => airline.masterModelsWithPHMAgreements.length === 0,
      )
      this.connectivityAirlines = this.connectivityIssues.map(connAirline => {
        const phm = nonPhmAirlines.find(
          phmAirline => connAirline.airlineId === phmAirline.airlineId,
        )
        return {
          ...connAirline,
          phm: !phm,
        }
      })
    },
    getConnectivityCheckFromSessionStorage() {
      const alertSeen = sessionStorage.getItem('connectivity_check')
      if (alertSeen) {
        this.connectivityCheck = [...JSON.parse(alertSeen)]
      }
    },
    setConnectivityCheckFromSessionStorage(index) {
      this.connectivityCheck[index] = !this.connectivityCheck[index]
      sessionStorage.setItem(
        'connectivity_check',
        JSON.stringify(this.connectivityCheck),
      )
    },
    handleCloseConnectivityAlert(val) {
      const index = val.color === 'error' ? 0 : 1
      this.pageAlertDefinitions[index].isActive = false
      this.setConnectivityCheckFromSessionStorage(index)
    },
    defaultDefinitions() {
      const texts = this.generateTextForBanner()
      const alertDefinitions = [...this.generateBannerObject(texts)]
      this.pageAlertDefinitions = [...alertDefinitions]
    },
  },
}
</script>

<style lang="scss">
.asc-page-wrapper {
  & .asc-content-wrapper--container {
    height: calc(100vh - 48px);
    position: fixed;
    top: 48px;
    background-color: $gray-100;

    &.full {
      left: $primary-navbar-width;
      right: 0;
    }

    &.collapsed {
      left: 48px;
      right: 0;
    }
  }
}
</style>
